import React, { useState, useEffect } from 'react';
import { 
  Upload, Download, Zap, Globe, ShoppingCart, 
  RefreshCw, Settings, FileText, PlusCircle, Trash2, Edit2,
  Save, X, CheckCircle, AlertCircle, ChevronDown, ChevronUp,
  Search, Filter, Package, TrendingUp, DollarSign, Languages
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001'}/api`;

export default function ProductFeedManager() {
  const [feeds, setFeeds] = useState([]);
  const [selectedFeed, setSelectedFeed] = useState(null);
  const [products, setProducts] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // View states
  const [view, setView] = useState('feeds'); // 'feeds', 'products', 'import', 'optimize', 'translate', 'export'
  
  // Modals
  const [showCreateFeedModal, setShowCreateFeedModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showOptimizeModal, setShowOptimizeModal] = useState(false);
  const [showTranslateModal, setShowTranslateModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showCampaignModal, setShowCampaignModal] = useState(false);
  
  // Form states
  const [newFeedName, setNewFeedName] = useState('');
  const [importType, setImportType] = useState('csv'); // 'csv', 'xml', 'shopify', 'woocommerce'
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [optimizeConfig, setOptimizeConfig] = useState({ fields: ['title'], optimization: 'seo' });
  const [translateConfig, setTranslateConfig] = useState({ sourceLanguage: 'nl', targetLanguages: [] });
  const [exportConfig, setExportConfig] = useState({ language: 'nl', market: 'NL' });
  const [campaignConfig, setCampaignConfig] = useState({ 
    campaignName: '', 
    budget: 10, 
    language: 'nl', 
    country: 'NL' 
  });
  
  // Shopify/WooCommerce config
  const [ecommerceConfig, setEcommerceConfig] = useState({
    shopify: { shopUrl: '', apiKey: '', apiPassword: '' },
    woocommerce: { siteUrl: '', consumerKey: '', consumerSecret: '' }
  });
  
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  
  // Available languages (reuse from existing system)
  const availableLanguages = [
    { code: 'nl', name: 'Nederlands', flag: '🇳🇱' },
    { code: 'en', name: 'English', flag: '🇺🇸' },
    { code: 'es', name: 'Español', flag: '🇪🇸' },
    { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
    { code: 'fr', name: 'Français', flag: '🇫🇷' },
    { code: 'it', name: 'Italiano', flag: '🇮🇹' },
    { code: 'pt', name: 'Português', flag: '🇵🇹' }
  ];
  
  useEffect(() => {
    fetchFeeds();
  }, []);
  
  useEffect(() => {
    if (selectedFeed) {
      fetchProducts(pagination.page);
    }
  }, [selectedFeed, pagination.page]);
  
  const fetchFeeds = async () => {
    try {
      const response = await fetch(`${API_URL}/feeds`);
      const data = await response.json();
      if (data.success) {
        setFeeds(data.feeds);
      }
    } catch (err) {
      setError('Failed to load feeds');
    }
  };
  
  const fetchProducts = async (page = 1) => {
    if (!selectedFeed) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/feeds/${selectedFeed.id}/products?page=${page}&limit=${pagination.limit}`);
      const data = await response.json();
      if (data.success) {
        setProducts(data.products);
        setPagination(data.pagination);
      }
    } catch (err) {
      setError('Failed to load products');
    } finally {
      setLoading(false);
    }
  };
  
  const createFeed = async () => {
    if (!newFeedName.trim()) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/feeds/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newFeedName })
      });
      const data = await response.json();
      if (data.success) {
        setSuccess(`Feed "${data.feed.name}" created successfully!`);
        setNewFeedName('');
        setShowCreateFeedModal(false);
        fetchFeeds();
      }
    } catch (err) {
      setError('Failed to create feed');
    } finally {
      setLoading(false);
    }
  };
  
  const deleteFeed = async (feedId) => {
    if (!window.confirm('Are you sure you want to delete this feed and all its products?')) return;
    
    try {
      const response = await fetch(`${API_URL}/feeds/${feedId}`, {
        method: 'DELETE'
      });
      const data = await response.json();
      if (data.success) {
        setSuccess('Feed deleted successfully');
        if (selectedFeed?.id === feedId) {
          setSelectedFeed(null);
          setProducts([]);
        }
        fetchFeeds();
      }
    } catch (err) {
      setError('Failed to delete feed');
    }
  };
  
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file || !selectedFeed) return;
    
    const formData = new FormData();
    formData.append('file', file);
    
    setLoading(true);
    try {
      const endpoint = importType === 'csv' ? 'import/csv' : 'import/xml';
      const response = await fetch(`${API_URL}/feeds/${selectedFeed.id}/${endpoint}`, {
        method: 'POST',
        body: formData
      });
      const data = await response.json();
      if (data.success) {
        setSuccess(`Imported ${data.imported} products successfully!`);
        setShowImportModal(false);
        fetchProducts();
        fetchFeeds();
      } else {
        setError(data.error || 'Import failed');
      }
    } catch (err) {
      setError('Failed to import file');
    } finally {
      setLoading(false);
    }
  };
  
  const handleEcommerceImport = async () => {
    if (!selectedFeed) return;
    
    const config = importType === 'shopify' ? ecommerceConfig.shopify : ecommerceConfig.woocommerce;
    
    if (importType === 'shopify' && (!config.shopUrl || !config.apiKey || !config.apiPassword)) {
      setError('Please fill in all Shopify credentials');
      return;
    }
    
    if (importType === 'woocommerce' && (!config.siteUrl || !config.consumerKey || !config.consumerSecret)) {
      setError('Please fill in all WooCommerce credentials');
      return;
    }
    
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/feeds/${selectedFeed.id}/import/${importType}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(config)
      });
      const data = await response.json();
      if (data.success) {
        setSuccess(`Imported ${data.imported} products from ${importType === 'shopify' ? 'Shopify' : 'WooCommerce'}!`);
        setShowImportModal(false);
        fetchProducts();
        fetchFeeds();
      } else {
        setError(data.error || 'Import failed');
      }
    } catch (err) {
      setError(`Failed to import from ${importType === 'shopify' ? 'Shopify' : 'WooCommerce'}`);
    } finally {
      setLoading(false);
    }
  };
  
  const optimizeProducts = async () => {
    if (!selectedFeed) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/feeds/${selectedFeed.id}/optimize`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fields: optimizeConfig.fields,
          productIds: selectedProducts,
          optimization: optimizeConfig.optimization
        })
      });
      const data = await response.json();
      if (data.success) {
        setSuccess(`Optimized ${data.optimized} products!`);
        setShowOptimizeModal(false);
        setSelectedProducts([]);
        fetchProducts();
      } else {
        setError(data.error || 'Optimization failed');
      }
    } catch (err) {
      setError('Failed to optimize products');
    } finally {
      setLoading(false);
    }
  };
  
  const translateFeed = async () => {
    if (!selectedFeed || translateConfig.targetLanguages.length === 0) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/feeds/${selectedFeed.id}/translate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(translateConfig)
      });
      const data = await response.json();
      if (data.success) {
        setSuccess(`Translated feed to ${data.languages.length} languages!`);
        setShowTranslateModal(false);
        fetchProducts();
        fetchFeeds();
      } else {
        setError(data.error || 'Translation failed');
      }
    } catch (err) {
      setError('Failed to translate feed');
    } finally {
      setLoading(false);
    }
  };
  
  const exportFeed = () => {
    if (!selectedFeed) return;
    
    const url = `${API_URL}/feeds/${selectedFeed.id}/export/merchant-center?language=${exportConfig.language}&market=${exportConfig.market}`;
    window.open(url, '_blank');
    setSuccess('Feed export started!');
    setShowExportModal(false);
  };
  
  const createShoppingCampaign = async () => {
    if (!selectedFeed || !campaignConfig.campaignName) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/feeds/${selectedFeed.id}/campaigns/shopping`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(campaignConfig)
      });
      const data = await response.json();
      if (data.success) {
        setSuccess(`Shopping campaign "${campaignConfig.campaignName}" created!`);
        setShowCampaignModal(false);
        setCampaignConfig({ campaignName: '', budget: 10, language: 'nl', country: 'NL' });
      } else {
        setError(data.error || 'Campaign creation failed');
      }
    } catch (err) {
      setError('Failed to create shopping campaign');
    } finally {
      setLoading(false);
    }
  };
  
  const toggleProductSelection = (productId) => {
    setSelectedProducts(prev => 
      prev.includes(productId) 
        ? prev.filter(id => id !== productId)
        : [...prev, productId]
    );
  };
  
  const selectAllProducts = () => {
    if (selectedProducts.length === products.length) {
      setSelectedProducts([]);
    } else {
      setSelectedProducts(products.map(p => p.id));
    }
  };
  
  const filteredProducts = products.filter(product => {
    const matchesSearch = !searchTerm || 
      product.title.nl?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.sku?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = !filterCategory || product.category?.includes(filterCategory);
    return matchesSearch && matchesCategory;
  });
  
  const renderFeedsList = () => (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 30 }}>
        <div>
          <h2 style={{ color: 'white', fontSize: 28, fontWeight: 700, margin: 0 }}>
            Product Feeds
          </h2>
          <p style={{ color: 'rgba(255,255,255,0.6)', marginTop: 8 }}>
            Manage your product feeds and export to Google Merchant Center
          </p>
        </div>
        <button
          onClick={() => setShowCreateFeedModal(true)}
          style={{
            padding: '12px 24px',
            background: 'linear-gradient(135deg, #ec4899 0%, #d946ef 100%)',
            border: 'none',
            borderRadius: 12,
            color: 'white',
            fontSize: 15,
            fontWeight: 600,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 8
          }}
        >
          <PlusCircle size={20} />
          Create New Feed
        </button>
      </div>
      
      {/* Feeds Grid */}
      {feeds.length === 0 ? (
        <div style={{
          background: 'rgba(255,255,255,0.05)',
          border: '2px dashed rgba(255,255,255,0.2)',
          borderRadius: 20,
          padding: 60,
          textAlign: 'center'
        }}>
          <Package size={48} style={{ color: 'rgba(255,255,255,0.3)', marginBottom: 16 }} />
          <h3 style={{ color: 'white', fontSize: 20, marginBottom: 8 }}>No feeds yet</h3>
          <p style={{ color: 'rgba(255,255,255,0.5)', marginBottom: 24 }}>
            Create your first product feed to get started
          </p>
          <button
            onClick={() => setShowCreateFeedModal(true)}
            style={{
              padding: '10px 20px',
              background: 'rgba(236,72,153,0.2)',
              border: '1px solid #ec4899',
              borderRadius: 8,
              color: '#ec4899',
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer'
            }}
          >
            Create Feed
          </button>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 20 }}>
          {feeds.map(feed => (
            <div
              key={feed.id}
              style={{
                background: 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: 16,
                padding: 24,
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
              onMouseOver={e => e.currentTarget.style.background = 'rgba(255,255,255,0.08)'}
              onMouseOut={e => e.currentTarget.style.background = 'rgba(255,255,255,0.05)'}
              onClick={() => {
                setSelectedFeed(feed);
                setView('products');
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <FileText size={24} style={{ color: '#ec4899' }} />
                  <div>
                    <h3 style={{ color: 'white', fontSize: 18, fontWeight: 600, margin: 0 }}>
                      {feed.name}
                    </h3>
                    <span style={{
                      color: 'rgba(255,255,255,0.5)',
                      fontSize: 12,
                      marginTop: 4,
                      display: 'block'
                    }}>
                      {feed.source === 'csv' ? '📄 CSV' : 
                       feed.source === 'xml' ? '📋 XML' :
                       feed.source === 'shopify' ? '🛍️ Shopify' :
                       feed.source === 'woocommerce' ? '🛒 WooCommerce' : '✏️ Manual'}
                    </span>
                  </div>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    deleteFeed(feed.id);
                  }}
                  style={{
                    background: 'rgba(239,68,68,0.1)',
                    border: '1px solid rgba(239,68,68,0.3)',
                    borderRadius: 8,
                    padding: 6,
                    cursor: 'pointer',
                    color: '#ef4444'
                  }}
                >
                  <Trash2 size={16} />
                </button>
              </div>
              
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12, marginBottom: 16 }}>
                <div style={{
                  background: 'rgba(236,72,153,0.1)',
                  border: '1px solid rgba(236,72,153,0.2)',
                  borderRadius: 8,
                  padding: 12
                }}>
                  <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 12, marginBottom: 4 }}>Products</div>
                  <div style={{ color: 'white', fontSize: 24, fontWeight: 700 }}>{feed.productCount}</div>
                </div>
                <div style={{
                  background: 'rgba(139,92,246,0.1)',
                  border: '1px solid rgba(139,92,246,0.2)',
                  borderRadius: 8,
                  padding: 12
                }}>
                  <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 12, marginBottom: 4 }}>Languages</div>
                  <div style={{ color: 'white', fontSize: 24, fontWeight: 700 }}>{feed.languages.length}</div>
                </div>
              </div>
              
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                {feed.languages.slice(0, 3).map(lang => {
                  const langInfo = availableLanguages.find(l => l.code === lang);
                  return (
                    <span
                      key={lang}
                      style={{
                        background: 'rgba(255,255,255,0.1)',
                        padding: '4px 8px',
                        borderRadius: 6,
                        fontSize: 11,
                        color: 'rgba(255,255,255,0.7)'
                      }}
                    >
                      {langInfo?.flag || ''} {lang.toUpperCase()}
                    </span>
                  );
                })}
                {feed.languages.length > 3 && (
                  <span style={{
                    background: 'rgba(255,255,255,0.1)',
                    padding: '4px 8px',
                    borderRadius: 6,
                    fontSize: 11,
                    color: 'rgba(255,255,255,0.7)'
                  }}>
                    +{feed.languages.length - 3}
                  </span>
                )}
              </div>
              
              <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: 12 }}>
                  Updated: {new Date(feed.updatedAt).toLocaleDateString()}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
  
  const renderProductsView = () => {
    if (!selectedFeed) return null;
    
    return (
      <div>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 30 }}>
          <div>
            <button
              onClick={() => {
                setSelectedFeed(null);
                setView('feeds');
              }}
              style={{
                background: 'none',
                border: 'none',
                color: '#ec4899',
                fontSize: 14,
                cursor: 'pointer',
                marginBottom: 8,
                display: 'flex',
                alignItems: 'center',
                gap: 4
              }}
            >
              ← Back to Feeds
            </button>
            <h2 style={{ color: 'white', fontSize: 28, fontWeight: 700, margin: 0 }}>
              {selectedFeed.name}
            </h2>
            <p style={{ color: 'rgba(255,255,255,0.6)', marginTop: 8 }}>
              {pagination.total} products
            </p>
          </div>
          
          <div style={{ display: 'flex', gap: 12 }}>
            <button
              onClick={() => setShowImportModal(true)}
              style={{
                padding: '10px 20px',
                background: 'rgba(59,130,246,0.2)',
                border: '1px solid #3b82f6',
                borderRadius: 10,
                color: '#3b82f6',
                fontSize: 14,
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 8
              }}
            >
              <Upload size={18} />
              Import Products
            </button>
            
            {products.length > 0 && (
              <>
                <button
                  onClick={() => setShowOptimizeModal(true)}
                  style={{
                    padding: '10px 20px',
                    background: 'rgba(139,92,246,0.2)',
                    border: '1px solid #8b5cf6',
                    borderRadius: 10,
                    color: '#8b5cf6',
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8
                  }}
                >
                  <Zap size={18} />
                  Optimize
                </button>
                
                <button
                  onClick={() => setShowTranslateModal(true)}
                  style={{
                    padding: '10px 20px',
                    background: 'rgba(16,185,129,0.2)',
                    border: '1px solid #10b981',
                    borderRadius: 10,
                    color: '#10b981',
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8
                  }}
                >
                  <Globe size={18} />
                  Translate
                </button>
                
                <button
                  onClick={() => setShowExportModal(true)}
                  style={{
                    padding: '10px 20px',
                    background: 'rgba(245,158,11,0.2)',
                    border: '1px solid #f59e0b',
                    borderRadius: 10,
                    color: '#f59e0b',
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8
                  }}
                >
                  <Download size={18} />
                  Export
                </button>
                
                <button
                  onClick={() => setShowCampaignModal(true)}
                  style={{
                    padding: '10px 20px',
                    background: 'linear-gradient(135deg, #ec4899 0%, #d946ef 100%)',
                    border: 'none',
                    borderRadius: 10,
                    color: 'white',
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8
                  }}
                >
                  <ShoppingCart size={18} />
                  Create Campaign
                </button>
              </>
            )}
          </div>
        </div>
        
        {selectedProducts.length > 0 && (
          <div style={{
            background: 'rgba(139,92,246,0.1)',
            border: '1px solid rgba(139,92,246,0.3)',
            borderRadius: 12,
            padding: 16,
            marginBottom: 20,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <span style={{ color: 'white', fontSize: 14 }}>
              {selectedProducts.length} products selected
            </span>
            <button
              onClick={() => setSelectedProducts([])}
              style={{
                background: 'none',
                border: 'none',
                color: '#8b5cf6',
                fontSize: 14,
                cursor: 'pointer'
              }}
            >
              Clear selection
            </button>
          </div>
        )}
        
        {/* Search and Filter */}
        <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
          <div style={{ flex: 1, position: 'relative' }}>
            <Search 
              size={18} 
              style={{ 
                position: 'absolute', 
                left: 12, 
                top: '50%', 
                transform: 'translateY(-50%)',
                color: 'rgba(255,255,255,0.4)'
              }} 
            />
            <input
              type="text"
              placeholder="Search products..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '12px 12px 12px 40px',
                background: 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: 10,
                color: 'white',
                fontSize: 14
              }}
            />
          </div>
          
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            style={{
              padding: '12px',
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: 10,
              color: 'white',
              fontSize: 14,
              minWidth: 150
            }}
          >
            <option value="">All Categories</option>
            {[...new Set(products.map(p => p.category).filter(Boolean))].map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        </div>
        
        {/* Products Table */}
        {loading ? (
          <div style={{ padding: 60, textAlign: 'center', color: 'rgba(255,255,255,0.6)' }}>
            <RefreshCw size={32} className="animate-spin" style={{ marginBottom: 16 }} />
            <div>Loading products...</div>
          </div>
        ) : filteredProducts.length === 0 ? (
          <div style={{
            background: 'rgba(255,255,255,0.05)',
            border: '2px dashed rgba(255,255,255,0.2)',
            borderRadius: 20,
            padding: 60,
            textAlign: 'center'
          }}>
            <Package size={48} style={{ color: 'rgba(255,255,255,0.3)', marginBottom: 16 }} />
            <h3 style={{ color: 'white', fontSize: 20, marginBottom: 8 }}>No products yet</h3>
            <p style={{ color: 'rgba(255,255,255,0.5)', marginBottom: 24 }}>
              Import products to get started
            </p>
            <button
              onClick={() => setShowImportModal(true)}
              style={{
                padding: '10px 20px',
                background: 'rgba(59,130,246,0.2)',
                border: '1px solid #3b82f6',
                borderRadius: 8,
                color: '#3b82f6',
                fontSize: 14,
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              Import Products
            </button>
          </div>
        ) : (
          <>
            <div style={{
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: 16,
              overflow: 'hidden'
            }}>
              {/* Table Header */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: '40px 80px 2fr 1fr 100px 100px 120px',
                padding: '16px',
                background: 'rgba(255,255,255,0.03)',
                borderBottom: '1px solid rgba(255,255,255,0.1)',
                gap: 12
              }}>
                <input
                  type="checkbox"
                  checked={selectedProducts.length === filteredProducts.length}
                  onChange={selectAllProducts}
                  style={{ cursor: 'pointer' }}
                />
                <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 13, fontWeight: 600 }}>Image</div>
                <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 13, fontWeight: 600 }}>Title</div>
                <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 13, fontWeight: 600 }}>Category</div>
                <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 13, fontWeight: 600 }}>Price</div>
                <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 13, fontWeight: 600 }}>Stock</div>
                <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 13, fontWeight: 600 }}>SKU</div>
              </div>
              
              {/* Table Body */}
              {filteredProducts.map((product, index) => (
                <div
                  key={product.id}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '40px 80px 2fr 1fr 100px 100px 120px',
                    padding: '16px',
                    borderBottom: index < filteredProducts.length - 1 ? '1px solid rgba(255,255,255,0.05)' : 'none',
                    alignItems: 'center',
                    gap: 12,
                    background: selectedProducts.includes(product.id) ? 'rgba(139,92,246,0.1)' : 'transparent'
                  }}
                >
                  <input
                    type="checkbox"
                    checked={selectedProducts.includes(product.id)}
                    onChange={() => toggleProductSelection(product.id)}
                    style={{ cursor: 'pointer' }}
                  />
                  <img
                    src={product.imageUrl || 'https://via.placeholder.com/80'}
                    alt={product.title.nl}
                    style={{
                      width: 60,
                      height: 60,
                      objectFit: 'cover',
                      borderRadius: 8,
                      border: '1px solid rgba(255,255,255,0.1)'
                    }}
                  />
                  <div>
                    <div style={{ color: 'white', fontSize: 14, marginBottom: 4 }}>
                      {product.title.nl || product.title[Object.keys(product.title)[0]]}
                    </div>
                    <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12 }}>
                      {product.brand}
                    </div>
                  </div>
                  <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: 13 }}>
                    {product.category || '-'}
                  </div>
                  <div style={{ color: 'white', fontSize: 14, fontWeight: 600 }}>
                    €{product.price.NL || product.price[Object.keys(product.price)[0]]}
                  </div>
                  <div>
                    <span style={{
                      padding: '4px 8px',
                      borderRadius: 6,
                      fontSize: 12,
                      fontWeight: 600,
                      background: product.availability === 'in stock' ? 'rgba(16,185,129,0.2)' : 'rgba(239,68,68,0.2)',
                      color: product.availability === 'in stock' ? '#10b981' : '#ef4444'
                    }}>
                      {product.availability}
                    </span>
                  </div>
                  <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 13 }}>
                    {product.sku || '-'}
                  </div>
                </div>
              ))}
            </div>
            
            {/* Pagination */}
            {pagination.pages > 1 && (
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: 12,
                marginTop: 24
              }}>
                <button
                  onClick={() => fetchProducts(pagination.page - 1)}
                  disabled={pagination.page === 1}
                  style={{
                    padding: '8px 16px',
                    background: 'rgba(255,255,255,0.05)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: 8,
                    color: 'white',
                    cursor: pagination.page === 1 ? 'not-allowed' : 'pointer',
                    opacity: pagination.page === 1 ? 0.5 : 1
                  }}
                >
                  Previous
                </button>
                <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: 14 }}>
                  Page {pagination.page} of {pagination.pages}
                </span>
                <button
                  onClick={() => fetchProducts(pagination.page + 1)}
                  disabled={pagination.page === pagination.pages}
                  style={{
                    padding: '8px 16px',
                    background: 'rgba(255,255,255,0.05)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: 8,
                    color: 'white',
                    cursor: pagination.page === pagination.pages ? 'not-allowed' : 'pointer',
                    opacity: pagination.page === pagination.pages ? 0.5 : 1
                  }}
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
    );
  };
  
  return (
    <div style={{ padding: 40, maxWidth: 1400, margin: '0 auto' }}>
      {/* Error/Success Messages */}
      {error && (
        <div style={{
          background: 'rgba(239,68,68,0.1)',
          border: '1px solid rgba(239,68,68,0.3)',
          borderRadius: 12,
          padding: 16,
          marginBottom: 20,
          display: 'flex',
          alignItems: 'center',
          gap: 12
        }}>
          <AlertCircle size={20} style={{ color: '#ef4444' }} />
          <span style={{ color: '#ef4444', fontSize: 14 }}>{error}</span>
          <button
            onClick={() => setError(null)}
            style={{
              marginLeft: 'auto',
              background: 'none',
              border: 'none',
              color: '#ef4444',
              cursor: 'pointer'
            }}
          >
            <X size={18} />
          </button>
        </div>
      )}
      
      {success && (
        <div style={{
          background: 'rgba(16,185,129,0.1)',
          border: '1px solid rgba(16,185,129,0.3)',
          borderRadius: 12,
          padding: 16,
          marginBottom: 20,
          display: 'flex',
          alignItems: 'center',
          gap: 12
        }}>
          <CheckCircle size={20} style={{ color: '#10b981' }} />
          <span style={{ color: '#10b981', fontSize: 14 }}>{success}</span>
          <button
            onClick={() => setSuccess(null)}
            style={{
              marginLeft: 'auto',
              background: 'none',
              border: 'none',
              color: '#10b981',
              cursor: 'pointer'
            }}
          >
            <X size={18} />
          </button>
        </div>
      )}
      
      {/* Main Content */}
      {view === 'feeds' && renderFeedsList()}
      {view === 'products' && renderProductsView()}
      
      {/* Create Feed Modal */}
      {showCreateFeedModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
            borderRadius: 20,
            padding: 40,
            maxWidth: 500,
            width: '100%'
          }}>
            <h3 style={{ color: 'white', fontSize: 24, fontWeight: 700, marginBottom: 24 }}>
              Create New Feed
            </h3>
            
            <div style={{ marginBottom: 24 }}>
              <label style={{ color: 'rgba(255,255,255,0.8)', fontSize: 14, marginBottom: 8, display: 'block' }}>
                Feed Name
              </label>
              <input
                type="text"
                value={newFeedName}
                onChange={(e) => setNewFeedName(e.target.value)}
                placeholder="e.g., Summer Collection 2024"
                style={{
                  width: '100%',
                  padding: 12,
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: 10,
                  color: 'white',
                  fontSize: 14
                }}
              />
            </div>
            
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowCreateFeedModal(false);
                  setNewFeedName('');
                }}
                style={{
                  padding: '10px 20px',
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: 10,
                  color: 'white',
                  fontSize: 14,
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={createFeed}
                disabled={!newFeedName.trim() || loading}
                style={{
                  padding: '10px 20px',
                  background: newFeedName.trim() ? 'linear-gradient(135deg, #ec4899 0%, #d946ef 100%)' : 'rgba(255,255,255,0.1)',
                  border: 'none',
                  borderRadius: 10,
                  color: 'white',
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: newFeedName.trim() ? 'pointer' : 'not-allowed',
                  opacity: newFeedName.trim() ? 1 : 0.5
                }}
              >
                {loading ? 'Creating...' : 'Create Feed'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Import Modal */}
      {showImportModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
            borderRadius: 20,
            padding: 40,
            maxWidth: 600,
            width: '100%',
            maxHeight: '80vh',
            overflowY: 'auto'
          }}>
            <h3 style={{ color: 'white', fontSize: 24, fontWeight: 700, marginBottom: 24 }}>
              Import Products
            </h3>
            
            {/* Import Type Selection */}
            <div style={{ marginBottom: 24 }}>
              <label style={{ color: 'rgba(255,255,255,0.8)', fontSize: 14, marginBottom: 12, display: 'block' }}>
                Select Import Source
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
                {['csv', 'xml', 'shopify', 'woocommerce'].map(type => (
                  <div
                    key={type}
                    onClick={() => setImportType(type)}
                    style={{
                      padding: 16,
                      background: importType === type ? 'rgba(59,130,246,0.2)' : 'rgba(255,255,255,0.05)',
                      border: `1px solid ${importType === type ? '#3b82f6' : 'rgba(255,255,255,0.1)'}`,
                      borderRadius: 10,
                      cursor: 'pointer',
                      textAlign: 'center'
                    }}
                  >
                    <div style={{ fontSize: 24, marginBottom: 8 }}>
                      {type === 'csv' && '📄'}
                      {type === 'xml' && '📋'}
                      {type === 'shopify' && '🛍️'}
                      {type === 'woocommerce' && '🛒'}
                    </div>
                    <div style={{ 
                      color: importType === type ? '#3b82f6' : 'white', 
                      fontSize: 14, 
                      fontWeight: 600,
                      textTransform: 'capitalize'
                    }}>
                      {type === 'woocommerce' ? 'WooCommerce' : type.toUpperCase()}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            {/* File Upload for CSV/XML */}
            {(importType === 'csv' || importType === 'xml') && (
              <div style={{ marginBottom: 24 }}>
                <label style={{ color: 'rgba(255,255,255,0.8)', fontSize: 14, marginBottom: 8, display: 'block' }}>
                  Upload {importType.toUpperCase()} File
                </label>
                <input
                  type="file"
                  accept={importType === 'csv' ? '.csv' : '.xml'}
                  onChange={handleFileUpload}
                  style={{
                    width: '100%',
                    padding: 12,
                    background: 'rgba(255,255,255,0.05)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: 10,
                    color: 'white',
                    fontSize: 14
                  }}
                />
              </div>
            )}
            
            {/* Shopify Config */}
            {importType === 'shopify' && (
              <div style={{ marginBottom: 24 }}>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ color: 'rgba(255,255,255,0.8)', fontSize: 14, marginBottom: 8, display: 'block' }}>
                    Shop URL
                  </label>
                  <input
                    type="text"
                    placeholder="your-shop.myshopify.com"
                    value={ecommerceConfig.shopify.shopUrl}
                    onChange={(e) => setEcommerceConfig({
                      ...ecommerceConfig,
                      shopify: { ...ecommerceConfig.shopify, shopUrl: e.target.value }
                    })}
                    style={{
                      width: '100%',
                      padding: 12,
                      background: 'rgba(255,255,255,0.05)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: 10,
                      color: 'white',
                      fontSize: 14
                    }}
                  />
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ color: 'rgba(255,255,255,0.8)', fontSize: 14, marginBottom: 8, display: 'block' }}>
                    API Key
                  </label>
                  <input
                    type="text"
                    value={ecommerceConfig.shopify.apiKey}
                    onChange={(e) => setEcommerceConfig({
                      ...ecommerceConfig,
                      shopify: { ...ecommerceConfig.shopify, apiKey: e.target.value }
                    })}
                    style={{
                      width: '100%',
                      padding: 12,
                      background: 'rgba(255,255,255,0.05)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: 10,
                      color: 'white',
                      fontSize: 14
                    }}
                  />
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ color: 'rgba(255,255,255,0.8)', fontSize: 14, marginBottom: 8, display: 'block' }}>
                    API Password
                  </label>
                  <input
                    type="password"
                    value={ecommerceConfig.shopify.apiPassword}
                    onChange={(e) => setEcommerceConfig({
                      ...ecommerceConfig,
                      shopify: { ...ecommerceConfig.shopify, apiPassword: e.target.value }
                    })}
                    style={{
                      width: '100%',
                      padding: 12,
                      background: 'rgba(255,255,255,0.05)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: 10,
                      color: 'white',
                      fontSize: 14
                    }}
                  />
                </div>
              </div>
            )}
            
            {/* WooCommerce Config */}
            {importType === 'woocommerce' && (
              <div style={{ marginBottom: 24 }}>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ color: 'rgba(255,255,255,0.8)', fontSize: 14, marginBottom: 8, display: 'block' }}>
                    Site URL
                  </label>
                  <input
                    type="text"
                    placeholder="https://your-site.com"
                    value={ecommerceConfig.woocommerce.siteUrl}
                    onChange={(e) => setEcommerceConfig({
                      ...ecommerceConfig,
                      woocommerce: { ...ecommerceConfig.woocommerce, siteUrl: e.target.value }
                    })}
                    style={{
                      width: '100%',
                      padding: 12,
                      background: 'rgba(255,255,255,0.05)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: 10,
                      color: 'white',
                      fontSize: 14
                    }}
                  />
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ color: 'rgba(255,255,255,0.8)', fontSize: 14, marginBottom: 8, display: 'block' }}>
                    Consumer Key
                  </label>
                  <input
                    type="text"
                    value={ecommerceConfig.woocommerce.consumerKey}
                    onChange={(e) => setEcommerceConfig({
                      ...ecommerceConfig,
                      woocommerce: { ...ecommerceConfig.woocommerce, consumerKey: e.target.value }
                    })}
                    style={{
                      width: '100%',
                      padding: 12,
                      background: 'rgba(255,255,255,0.05)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: 10,
                      color: 'white',
                      fontSize: 14
                    }}
                  />
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ color: 'rgba(255,255,255,0.8)', fontSize: 14, marginBottom: 8, display: 'block' }}>
                    Consumer Secret
                  </label>
                  <input
                    type="password"
                    value={ecommerceConfig.woocommerce.consumerSecret}
                    onChange={(e) => setEcommerceConfig({
                      ...ecommerceConfig,
                      woocommerce: { ...ecommerceConfig.woocommerce, consumerSecret: e.target.value }
                    })}
                    style={{
                      width: '100%',
                      padding: 12,
                      background: 'rgba(255,255,255,0.05)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: 10,
                      color: 'white',
                      fontSize: 14
                    }}
                  />
                </div>
              </div>
            )}
            
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowImportModal(false)}
                style={{
                  padding: '10px 20px',
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: 10,
                  color: 'white',
                  fontSize: 14,
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              {(importType === 'shopify' || importType === 'woocommerce') && (
                <button
                  onClick={handleEcommerceImport}
                  disabled={loading}
                  style={{
                    padding: '10px 20px',
                    background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                    border: 'none',
                    borderRadius: 10,
                    color: 'white',
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: loading ? 'wait' : 'pointer'
                  }}
                >
                  {loading ? 'Importing...' : 'Import Products'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Optimize Modal */}
      {showOptimizeModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
            borderRadius: 20,
            padding: 40,
            maxWidth: 500,
            width: '100%'
          }}>
            <h3 style={{ color: 'white', fontSize: 24, fontWeight: 700, marginBottom: 24 }}>
              Optimize Products with AI
            </h3>
            
            <div style={{ marginBottom: 24 }}>
              <label style={{ color: 'rgba(255,255,255,0.8)', fontSize: 14, marginBottom: 12, display: 'block' }}>
                Fields to Optimize
              </label>
              <div style={{ display: 'flex', gap: 12 }}>
                {['title', 'description'].map(field => (
                  <label
                    key={field}
                    style={{
                      flex: 1,
                      padding: 12,
                      background: optimizeConfig.fields.includes(field) ? 'rgba(139,92,246,0.2)' : 'rgba(255,255,255,0.05)',
                      border: `1px solid ${optimizeConfig.fields.includes(field) ? '#8b5cf6' : 'rgba(255,255,255,0.1)'}`,
                      borderRadius: 10,
                      cursor: 'pointer',
                      textAlign: 'center',
                      color: 'white',
                      fontSize: 14,
                      textTransform: 'capitalize'
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={optimizeConfig.fields.includes(field)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setOptimizeConfig({
                            ...optimizeConfig,
                            fields: [...optimizeConfig.fields, field]
                          });
                        } else {
                          setOptimizeConfig({
                            ...optimizeConfig,
                            fields: optimizeConfig.fields.filter(f => f !== field)
                          });
                        }
                      }}
                      style={{ marginRight: 8 }}
                    />
                    {field}
                  </label>
                ))}
              </div>
            </div>
            
            <div style={{ marginBottom: 24 }}>
              <label style={{ color: 'rgba(255,255,255,0.8)', fontSize: 14, marginBottom: 8, display: 'block' }}>
                Optimization Type
              </label>
              <select
                value={optimizeConfig.optimization}
                onChange={(e) => setOptimizeConfig({ ...optimizeConfig, optimization: e.target.value })}
                style={{
                  width: '100%',
                  padding: 12,
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: 10,
                  color: 'white',
                  fontSize: 14
                }}
              >
                <option value="seo">SEO Optimization</option>
                <option value="conversion">Conversion Focused</option>
                <option value="character-limit">Character Limit Compliance</option>
              </select>
            </div>
            
            <div style={{
              background: 'rgba(139,92,246,0.1)',
              border: '1px solid rgba(139,92,246,0.3)',
              borderRadius: 10,
              padding: 12,
              marginBottom: 24
            }}>
              <span style={{ color: 'rgba(255,255,255,0.8)', fontSize: 13 }}>
                {selectedProducts.length > 0 
                  ? `${selectedProducts.length} products selected for optimization` 
                  : 'All products will be optimized'}
              </span>
            </div>
            
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowOptimizeModal(false)}
                style={{
                  padding: '10px 20px',
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: 10,
                  color: 'white',
                  fontSize: 14,
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={optimizeProducts}
                disabled={loading || optimizeConfig.fields.length === 0}
                style={{
                  padding: '10px 20px',
                  background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                  border: 'none',
                  borderRadius: 10,
                  color: 'white',
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: loading || optimizeConfig.fields.length === 0 ? 'not-allowed' : 'pointer',
                  opacity: optimizeConfig.fields.length === 0 ? 0.5 : 1
                }}
              >
                {loading ? 'Optimizing...' : 'Optimize with AI'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Translate Modal */}
      {showTranslateModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
            borderRadius: 20,
            padding: 40,
            maxWidth: 600,
            width: '100%'
          }}>
            <h3 style={{ color: 'white', fontSize: 24, fontWeight: 700, marginBottom: 24 }}>
              Translate Feed
            </h3>
            
            <div style={{ marginBottom: 24 }}>
              <label style={{ color: 'rgba(255,255,255,0.8)', fontSize: 14, marginBottom: 8, display: 'block' }}>
                Source Language
              </label>
              <select
                value={translateConfig.sourceLanguage}
                onChange={(e) => setTranslateConfig({ ...translateConfig, sourceLanguage: e.target.value })}
                style={{
                  width: '100%',
                  padding: 12,
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: 10,
                  color: 'white',
                  fontSize: 14
                }}
              >
                {availableLanguages.map(lang => (
                  <option key={lang.code} value={lang.code}>
                    {lang.flag} {lang.name}
                  </option>
                ))}
              </select>
            </div>
            
            <div style={{ marginBottom: 24 }}>
              <label style={{ color: 'rgba(255,255,255,0.8)', fontSize: 14, marginBottom: 12, display: 'block' }}>
                Target Languages
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
                {availableLanguages
                  .filter(lang => lang.code !== translateConfig.sourceLanguage)
                  .map(lang => (
                    <label
                      key={lang.code}
                      style={{
                        padding: 12,
                        background: translateConfig.targetLanguages.includes(lang.code) 
                          ? 'rgba(16,185,129,0.2)' 
                          : 'rgba(255,255,255,0.05)',
                        border: `1px solid ${
                          translateConfig.targetLanguages.includes(lang.code) 
                            ? '#10b981' 
                            : 'rgba(255,255,255,0.1)'
                        }`,
                        borderRadius: 10,
                        cursor: 'pointer',
                        color: 'white',
                        fontSize: 14,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={translateConfig.targetLanguages.includes(lang.code)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setTranslateConfig({
                              ...translateConfig,
                              targetLanguages: [...translateConfig.targetLanguages, lang.code]
                            });
                          } else {
                            setTranslateConfig({
                              ...translateConfig,
                              targetLanguages: translateConfig.targetLanguages.filter(l => l !== lang.code)
                            });
                          }
                        }}
                      />
                      {lang.flag} {lang.name}
                    </label>
                  ))}
              </div>
            </div>
            
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowTranslateModal(false)}
                style={{
                  padding: '10px 20px',
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: 10,
                  color: 'white',
                  fontSize: 14,
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={translateFeed}
                disabled={loading || translateConfig.targetLanguages.length === 0}
                style={{
                  padding: '10px 20px',
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  border: 'none',
                  borderRadius: 10,
                  color: 'white',
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: loading || translateConfig.targetLanguages.length === 0 ? 'not-allowed' : 'pointer',
                  opacity: translateConfig.targetLanguages.length === 0 ? 0.5 : 1
                }}
              >
                {loading ? 'Translating...' : 'Translate Feed'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Export Modal */}
      {showExportModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
            borderRadius: 20,
            padding: 40,
            maxWidth: 500,
            width: '100%'
          }}>
            <h3 style={{ color: 'white', fontSize: 24, fontWeight: 700, marginBottom: 24 }}>
              Export to Google Merchant Center
            </h3>
            
            <div style={{ marginBottom: 16 }}>
              <label style={{ color: 'rgba(255,255,255,0.8)', fontSize: 14, marginBottom: 8, display: 'block' }}>
                Language
              </label>
              <select
                value={exportConfig.language}
                onChange={(e) => setExportConfig({ ...exportConfig, language: e.target.value })}
                style={{
                  width: '100%',
                  padding: 12,
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: 10,
                  color: 'white',
                  fontSize: 14
                }}
              >
                {availableLanguages.map(lang => (
                  <option key={lang.code} value={lang.code}>
                    {lang.flag} {lang.name}
                  </option>
                ))}
              </select>
            </div>
            
            <div style={{ marginBottom: 24 }}>
              <label style={{ color: 'rgba(255,255,255,0.8)', fontSize: 14, marginBottom: 8, display: 'block' }}>
                Market
              </label>
              <select
                value={exportConfig.market}
                onChange={(e) => setExportConfig({ ...exportConfig, market: e.target.value })}
                style={{
                  width: '100%',
                  padding: 12,
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: 10,
                  color: 'white',
                  fontSize: 14
                }}
              >
                <option value="NL">🇳🇱 Netherlands</option>
                <option value="BE">🇧🇪 Belgium</option>
                <option value="DE">🇩🇪 Germany</option>
                <option value="FR">🇫🇷 France</option>
                <option value="ES">🇪🇸 Spain</option>
                <option value="IT">🇮🇹 Italy</option>
              </select>
            </div>
            
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowExportModal(false)}
                style={{
                  padding: '10px 20px',
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: 10,
                  color: 'white',
                  fontSize: 14,
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={exportFeed}
                style={{
                  padding: '10px 20px',
                  background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                  border: 'none',
                  borderRadius: 10,
                  color: 'white',
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                Download XML
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Create Campaign Modal */}
      {showCampaignModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
            borderRadius: 20,
            padding: 40,
            maxWidth: 500,
            width: '100%'
          }}>
            <h3 style={{ color: 'white', fontSize: 24, fontWeight: 700, marginBottom: 24 }}>
              Create Shopping Campaign
            </h3>
            
            <div style={{ marginBottom: 16 }}>
              <label style={{ color: 'rgba(255,255,255,0.8)', fontSize: 14, marginBottom: 8, display: 'block' }}>
                Campaign Name
              </label>
              <input
                type="text"
                value={campaignConfig.campaignName}
                onChange={(e) => setCampaignConfig({ ...campaignConfig, campaignName: e.target.value })}
                placeholder="e.g., Summer Products 2024"
                style={{
                  width: '100%',
                  padding: 12,
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: 10,
                  color: 'white',
                  fontSize: 14
                }}
              />
            </div>
            
            <div style={{ marginBottom: 16 }}>
              <label style={{ color: 'rgba(255,255,255,0.8)', fontSize: 14, marginBottom: 8, display: 'block' }}>
                Daily Budget (€)
              </label>
              <input
                type="number"
                value={campaignConfig.budget}
                onChange={(e) => setCampaignConfig({ ...campaignConfig, budget: parseFloat(e.target.value) })}
                min="1"
                style={{
                  width: '100%',
                  padding: 12,
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: 10,
                  color: 'white',
                  fontSize: 14
                }}
              />
            </div>
            
            <div style={{ marginBottom: 16 }}>
              <label style={{ color: 'rgba(255,255,255,0.8)', fontSize: 14, marginBottom: 8, display: 'block' }}>
                Country
              </label>
              <select
                value={campaignConfig.country}
                onChange={(e) => setCampaignConfig({ ...campaignConfig, country: e.target.value })}
                style={{
                  width: '100%',
                  padding: 12,
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: 10,
                  color: 'white',
                  fontSize: 14
                }}
              >
                <option value="NL">🇳🇱 Netherlands</option>
                <option value="BE">🇧🇪 Belgium</option>
                <option value="DE">🇩🇪 Germany</option>
                <option value="FR">🇫🇷 France</option>
                <option value="ES">🇪🇸 Spain</option>
                <option value="IT">🇮🇹 Italy</option>
              </select>
            </div>
            
            <div style={{
              background: 'rgba(236,72,153,0.1)',
              border: '1px solid rgba(236,72,153,0.3)',
              borderRadius: 10,
              padding: 12,
              marginBottom: 24
            }}>
              <span style={{ color: 'rgba(255,255,255,0.8)', fontSize: 13 }}>
                📦 {selectedFeed?.productCount || 0} products will be used in this campaign
              </span>
            </div>
            
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowCampaignModal(false)}
                style={{
                  padding: '10px 20px',
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: 10,
                  color: 'white',
                  fontSize: 14,
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={createShoppingCampaign}
                disabled={loading || !campaignConfig.campaignName}
                style={{
                  padding: '10px 20px',
                  background: 'linear-gradient(135deg, #ec4899 0%, #d946ef 100%)',
                  border: 'none',
                  borderRadius: 10,
                  color: 'white',
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: loading || !campaignConfig.campaignName ? 'not-allowed' : 'pointer',
                  opacity: !campaignConfig.campaignName ? 0.5 : 1
                }}
              >
                {loading ? 'Creating...' : 'Create Campaign'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


